import api from '../config/api';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';

/**
 * Category Service - API client for the product sub-categories feature.
 * Mirrors inventoryService.js: uses the shared `api` axios instance which
 * attaches the JWT (`x-auth-token`) via its request interceptor.
 */

/**
 * Fetch the brand's two-level category tree with per-node product counts.
 * @param {number|string} brandId
 * @returns {Promise<{categories: Array<{category: string, product_count: number,
 *   sub_categories: Array<{sub_category: string, product_count: number}>}>}>}
 */
export const getCategories = async (brandId) => {
  const response = await api.get(`${API_BASE}/api/brands/${brandId}/categories`);
  return response.data;
};

/**
 * Assign category / sub_category to one or more products (keyed by product_master id).
 * Per-field semantics (server-side): '' / omitted = leave unchanged;
 * the sentinel '__CLEAR__' clears that field.
 * @param {number|string} brandId
 * @param {Array<{id: number, category?: string, sub_category?: string}>} updates
 * @returns {Promise<{success: boolean, updated: number, failed: number, errors: Array}>}
 */
export const assignCategory = async (brandId, updates) => {
  const response = await api.post(
    `${API_BASE}/api/brands/${brandId}/products/master/bulk-update`,
    { updates }
  );
  return response.data;
};

/**
 * Create a category (omit sub_category) or a sub-category (parent must exist).
 * @returns {Promise<{id:number, category:string, sub_category:string}>}
 */
export const createCategory = async (brandId, { category, sub_category = '' }) => {
  const response = await api.post(`${API_BASE}/api/brands/${brandId}/categories`, { category, sub_category });
  return response.data;
};

/**
 * Rename (cascades to assigned products) / reorder / (de)activate a node by id.
 * @param {object} changes - any of { category, sub_category, sort_order, is_active }
 */
export const updateCategory = async (brandId, id, changes) => {
  const response = await api.put(`${API_BASE}/api/brands/${brandId}/categories/${id}`, changes);
  return response.data;
};

/**
 * Soft-delete a node. Rejects (409) with `{ products }` or `{ sub_categories }`
 * counts if it is still in use.
 */
export const deleteCategory = async (brandId, id) => {
  const response = await api.delete(`${API_BASE}/api/brands/${brandId}/categories/${id}`);
  return response.data;
};
